import React, { createContext, useContext } from 'react';

import { Loading } from 'components';
import { useParamValues, useQueryValues } from 'hooks/url.hook';
import { useFetchZoneWithoutAuthQuery } from 'store/apis';
import { getCookies } from 'store/apis/apis.util';
import { UrlError } from './url-error';

type SuspendServicePageContextType = {
  zoneName: string;
  buildingName: string;
  orgSlug: string;
  buildingId: number;
  scheduleId: number;
  userId: number;
};

const defaultSuspendServicePageContextValue: SuspendServicePageContextType = {
  zoneName: '',
  buildingName: '',
  orgSlug: '',
  buildingId: 0,
  scheduleId: 0,
  userId: 0,
};

export const SuspendServicePageContext = createContext<SuspendServicePageContextType>(
  defaultSuspendServicePageContextValue,
);

interface SuspendServicePageContextProviderProps {
  children?: any;
}

export const SuspendServicePageContextProvider = ({ children = null }: SuspendServicePageContextProviderProps) => {
  const { zoneId: zoneIdStr = '' } = useParamValues(['zoneId']);
  const {
    org: orgSlug = '',
    schedule: scheduleIdStr = '',
    user: userIdStr = '',
  } = useQueryValues(['org', 'schedule', 'user']);

  const zoneIdTemp = parseInt(zoneIdStr, 10);
  const zoneId = Number.isNaN(zoneIdTemp) ? 0 : zoneIdTemp;

  const scheduleIdTemp = parseInt(scheduleIdStr, 10);
  const scheduleId = Number.isNaN(scheduleIdTemp) ? 0 : scheduleIdTemp;

  const userIdTemp = parseInt(userIdStr, 10);
  const userId = Number.isNaN(userIdTemp) ? 0 : userIdTemp;

  const {
    data: { code = '', name = '', address: buildingName = '', building_id: buildingId } = {},
    isLoading: loading_zone,
  } = useFetchZoneWithoutAuthQuery({
    id: zoneId,
  });

  const zoneName = `#${code} - ${name}`;

  const { cleancodeRedirectToken } = getCookies();

  if (!cleancodeRedirectToken) {
    return <UrlError />;
  }

  if (loading_zone) {
    return <Loading text="One moment, loading data for configuring service interruption form..." />;
  }

  if (!zoneId || !orgSlug || !buildingId || !scheduleId || !userId) {
    return <UrlError />;
  }

  return (
    <SuspendServicePageContext.Provider value={{ zoneName, buildingName, orgSlug, buildingId, scheduleId, userId }}>
      {children}
    </SuspendServicePageContext.Provider>
  );
};

export const useSuspendServicePageContext = () => {
  return useContext(SuspendServicePageContext);
};
